import { PUBLIC_URL } from "@/lib/publicUrl";
import { Link } from "react-router-dom";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { fileUrl, thumbUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowRight, Layers, Palette, Boxes, Landmark, QrCode, Download, Copy } from "lucide-react";
import { ReviewCard, StarSummary } from "@/components/ReviewCard";
import { toast } from "sonner";
import { WelcomeBanner } from "@/components/WelcomeBanner";
import { ServicesList } from "@/components/ServicesList";

const HERO = "https://images.unsplash.com/photo-1523381294911-8d3cead13475?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Home() {
  const { data: designs = [] } = useQuery({ queryKey: ["designs"], queryFn: async () => (await api.get("/designs")).data });
  const { data: featured } = useQuery({ queryKey: ["featured-collection"], queryFn: async () => (await api.get("/collections/featured")).data, retry: false });
  const { data: gallery = [] } = useQuery({ queryKey: ["gallery"], queryFn: async () => (await api.get("/gallery")).data });
  const { data: reviews = [] } = useQuery({ queryKey: ["reviews"], queryFn: async () => (await api.get("/reviews")).data });
  const { data: cats = [] } = useQuery({ queryKey: ["categories"], queryFn: async () => (await api.get("/categories")).data });
  const shopFolders = cats.filter((c) => !c.parent_id && (c.design_count || 0) > 0 && c.thumbnail).slice(0, 8);
  const [lightbox, setLightbox] = useState(null);

  return (
    <div>
      <WelcomeBanner />
      {/* HERO */}
      <section className="relative overflow-hidden grain">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0c0d10]/70 via-[#0c0d10]/85 to-[#0c0d10]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-16">
          <div className="max-w-3xl animate-fade-up">
            <span className="inline-flex items-center gap-2 text-xs font-mono-p uppercase tracking-widest text-[#22c3d6] border border-[#22c3d6]/30 rounded-full px-3 py-1">
              <span className="w-1.5 h-1.5 rounded-full bg-[#22c3d6] animate-pulse" /> DTF · Gang Sheets · Custom Merch
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-none mt-6 text-white">
              Print your Vision, <span style={{ backgroundImage: "linear-gradient(90deg,#ff6a13,#ffd23f,#4fd23a,#22c3d6,#ff2fb0)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>Perfectly Crafted.</span>
            </h1>
            <p className="text-zinc-300 text-base sm:text-lg mt-6 max-w-xl leading-relaxed">
              Build gang sheets, browse our design catalog, and order custom apparel — with live inventory and instant online checkout.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/builder"><Button data-testid="hero-builder-btn" className="bg-[#ff6a13] hover:bg-[#ff8a3d] text-white rounded-full h-12 px-6 font-semibold active:scale-95 transition-transform">Launch Gang Sheet Builder <ArrowRight size={18} className="ml-1" /></Button></Link>
              <Link to="/catalog"><Button data-testid="hero-catalog-btn" variant="outline" className="rounded-full h-12 px-6 font-semibold border-zinc-700 bg-transparent text-white hover:bg-zinc-900">Browse Designs</Button></Link>
            </div>
          </div>
        </div>
      </section>

      <ServicesList />

      {shopFolders.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12" data-testid="home-shop-by-folder">
          <div className="flex items-end justify-between mb-5">
            <div>
              <span className="text-xs font-mono-p uppercase tracking-widest text-[#22c3d6]">Browse</span>
              <h2 className="font-display text-2xl sm:text-3xl font-black text-white mt-1">Shop by folder</h2>
            </div>
            <Link to="/catalog" className="text-sm text-[#ff6a13] hover:text-white transition-colors font-semibold shrink-0">View all →</Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
            {shopFolders.map((s) => (
              <Link key={s.category_id} to={`/catalog?section=${s.category_id}`} data-testid={`home-folder-${s.category_id}`}
                className="group relative rounded-2xl overflow-hidden border border-zinc-800 hover:border-[#ff6a13]/60 bg-[#12141a] transition-all">
                {s.has_new && <span className="absolute top-1.5 right-1.5 z-10 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-[#22c3d6] text-black" data-testid={`home-folder-new-${s.category_id}`}>New</span>}
                <div className="checker h-24 flex items-center justify-center p-3">
                  <img src={thumbUrl(s.thumbnail, 480)} alt={s.name} loading="lazy" className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform" />
                </div>
                <p className="text-xs font-semibold text-white truncate px-2.5 py-2">{s.name}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {featured && featured.collection_id && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10" data-testid="home-featured-collection">
          <Link to={`/collections/${featured.collection_id}`} className="group relative block overflow-hidden rounded-2xl border border-zinc-800 hover:border-[#ff6a13] transition-colors">
            {featured.image_url && <img src={fileUrl(featured.image_url)} alt={featured.name} className="w-full h-48 sm:h-60 object-cover opacity-70 group-hover:opacity-90 transition-opacity" />}
            <div className={`${featured.image_url ? "absolute inset-0" : "min-h-[12rem]"} bg-gradient-to-r from-[#0c0d10] via-[#0c0d10]/70 to-transparent flex flex-col justify-center p-8`}>
              <span className="text-xs font-mono-p uppercase tracking-widest text-[#22c3d6]">Featured Collection</span>
              <h2 className="font-display text-3xl sm:text-4xl font-black text-white mt-2">{featured.name}</h2>
              <span className="inline-flex items-center gap-1 text-[#ff6a13] font-semibold mt-3">Shop the drop <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" /></span>
            </div>
          </Link>
        </section>
      )}

      {lightbox && (
        <div className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-6 cursor-zoom-out" onClick={() => setLightbox(null)} data-testid="gallery-lightbox">
          <div className="max-w-3xl max-h-[85vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img src={fileUrl(lightbox.image_url)} alt={lightbox.caption || ""} className="max-w-full max-h-[75vh] rounded-xl border border-zinc-800 object-contain" />
            {lightbox.caption && <p className="text-zinc-300 mt-3 text-sm">{lightbox.caption}</p>}
            <button onClick={() => setLightbox(null)} data-testid="lightbox-close" className="mt-4 text-zinc-400 hover:text-white text-sm font-mono-p">Close ✕</button>
          </div>
        </div>
      )}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: Layers, t: "Gang Sheet Builder", d: "Drag, resize & auto-nest designs on 12\" & 17\" rolls with live pricing." },
            { icon: Palette, t: "Design Catalog", d: "Curated print-ready artwork across every style." },
            { icon: Boxes, t: "Live Inventory", d: "Real-time stock per color, type & size variation." },
            { icon: Landmark, t: "Direct Deposit Checkout", d: "Pay securely by direct bank deposit — order number as your reference." },
          ].map((f) => (
            <div key={f.t} className="bg-[#12141a] border border-zinc-800 rounded-2xl p-6 cursor-default select-none" data-testid={`home-feature-${f.t.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="w-11 h-11 rounded-xl bg-[#1e222d] border border-zinc-800 flex items-center justify-center text-[#ff6a13]"><f.icon size={20} /></div>
              <h3 className="font-display text-lg font-bold text-white mt-4">{f.t}</h3>
              <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CATALOG PREVIEW */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-white">Fresh from the catalog</h2>
            <p className="text-zinc-400 mt-2">Hand-picked print-ready graphics ready to press.</p>
          </div>
          <Link to="/catalog" className="text-[#ff6a13] hover:text-[#ff8a3d] text-sm font-semibold hidden sm:flex items-center gap-1">View all <ArrowRight size={15} /></Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {designs.slice(0, 4).map((d) => (
            <Link to="/catalog" key={d.design_id} className="checker rounded-xl border border-zinc-800 aspect-square flex items-center justify-center overflow-hidden hover:border-[#ff6a13]/60 transition-colors group">
              <img src={fileUrl(d.image_url)} alt={d.title} className="max-w-full max-h-full object-contain p-2 group-hover:scale-105 transition-transform" />
            </Link>
          ))}
        </div>
      </section>

      {gallery.find((g) => g.featured) && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12" data-testid="home-featured-creation">
          {(() => { const f = gallery.find((g) => g.featured); return (
            <button onClick={() => setLightbox(f)} className="group relative block w-full overflow-hidden rounded-2xl border border-zinc-800 hover:border-[#ff6a13] transition-colors text-left" data-testid="featured-creation-tile">
              <div className="checker w-full aspect-[16/7] flex items-center justify-center overflow-hidden">
                <img src={fileUrl(f.image_url)} alt={f.caption || "Featured creation"} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#0c0d10] via-[#0c0d10]/30 to-transparent flex flex-col justify-end p-6 sm:p-8">
                <span className="text-xs font-mono-p uppercase tracking-widest text-[#22c3d6]">Featured Creation</span>
                {f.caption && <h2 className="font-display text-2xl sm:text-4xl font-black text-white mt-2 max-w-2xl">{f.caption}</h2>}
              </div>
            </button>
          ); })()}
        </section>
      )}
      {gallery.length > 0 && (
        <section className="py-16 overflow-hidden border-y border-zinc-900" data-testid="home-gallery">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8 flex items-end justify-between gap-4 flex-wrap">
            <div>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-white">Our Creations</h2>
              <p className="text-zinc-400 mt-2">A look at recent designs we've printed for our customers.</p>
            </div>
            <Link to="/builder"><Button data-testid="gallery-cta-btn" className="bg-[#ff6a13] hover:bg-[#ff8a3d] text-white rounded-full h-11 px-5 font-semibold active:scale-95 transition-transform">Start your order <ArrowRight size={16} className="ml-1" /></Button></Link>
          </div>
          <div className="relative">
            <div className="flex gap-4 w-max animate-marquee px-4">
              {(() => { const scroll = gallery.filter((g) => !g.featured); const list = scroll.length ? scroll : gallery; return [...list, ...list].map((g, i) => (
                <button key={i} onClick={() => setLightbox(g)} className="w-56 shrink-0 rounded-xl overflow-hidden border border-zinc-800 bg-[#12141a] text-left hover:border-[#ff6a13]/60 transition-colors" data-testid={i < list.length ? `gallery-item-${i}` : undefined}>
                  <div className="aspect-square checker flex items-center justify-center overflow-hidden">
                    <img src={fileUrl(g.image_url)} alt={g.caption || "Our creation"} className="w-full h-full object-cover" />
                  </div>
                  {g.caption && <p className="text-xs text-zinc-400 px-3 py-2 truncate">{g.caption}</p>}
                </button>
              )); })()}
            </div>
          </div>
        </section>
      )}

      {/* REVIEWS */}
      {reviews.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20" data-testid="home-reviews">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-white">What our customers say</h2>
              <p className="text-zinc-400 mt-2">Real reviews from real print jobs.</p>
              <StarSummary reviews={reviews} />
            </div>
            <Link to="/reviews" className="text-[#ff6a13] hover:text-[#ff8a3d] text-sm font-semibold hidden sm:flex items-center gap-1" data-testid="home-reviews-viewall">Read all reviews <ArrowRight size={15} /></Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
            {reviews.slice(0, 6).map((r) => <ReviewCard key={r.review_id} r={r} />)}
          </div>
        </section>
      )}

      {/* SHARE / QR */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20" data-testid="home-qr-section">
        {(() => {
          const SITE_URL = PUBLIC_URL;
          const BACKEND = process.env.REACT_APP_BACKEND_URL;
          const qrSrc = `${BACKEND}/api/qr?data=${encodeURIComponent(SITE_URL)}`;
          const qrDownload = `${qrSrc}&download=true`;
          const copyLink = async () => { try { await navigator.clipboard.writeText(SITE_URL); toast.success("Store link copied"); } catch { toast.error("Copy failed"); } };
          return (
            <div className="rounded-3xl border border-zinc-800 bg-[#12141a] p-8 sm:p-10 flex flex-col sm:flex-row items-center gap-8">
              <div className="bg-white rounded-2xl p-4 shrink-0">
                <img src={qrSrc} alt="All-Ways Graphic store QR code" width={200} height={200} data-testid="home-qr-image" className="w-[200px] h-[200px]" />
              </div>
              <div className="flex-1 min-w-0 text-center sm:text-left">
                <span className="inline-flex items-center gap-2 text-xs font-mono-p uppercase tracking-widest text-[#22c3d6] border border-[#22c3d6]/30 rounded-full px-3 py-1">
                  <QrCode size={13} /> Scan · Save · Share
                </span>
                <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-white mt-4">Take our store with you</h2>
                <p className="text-zinc-400 mt-2 max-w-lg">Scan this code with your phone camera to open our store instantly — or download the QR to save, print, or share with a friend.</p>
                <div className="flex flex-wrap gap-3 mt-6 justify-center sm:justify-start">
                  <a href={qrDownload} download="all-ways-graphic-qr.png" data-testid="home-qr-download"><Button className="bg-[#ff6a13] hover:bg-[#ff8a3d] text-white rounded-full h-12 px-6 font-semibold active:scale-95 transition-transform"><Download size={16} className="mr-2" />Download QR code</Button></a>
                  <Button onClick={copyLink} data-testid="home-qr-copy" variant="outline" className="rounded-full h-12 px-6 border-zinc-700 bg-transparent text-white hover:bg-zinc-800 font-semibold"><Copy size={16} className="mr-2" />Copy store link</Button>
                </div>
              </div>
            </div>
          );
        })()}
      </section>
    </div>
  );
}
